import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ChevronRight, Maximize, Star, Users } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import { Link } from 'gatsby'
import { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  category: string
  className?: string
  image?: ImageProps
  languageCode: string
  mainService?: string
  pax?: string
  size?: string
  title: string
  URL: string
}

export const Room = memo(function Room({
  className,
  image,
  languageCode,
  mainService,
  pax,
  size,
  title,
  URL,
}: Props) {
  if (!title || !URL) {
    return null
  }

  return (
    <Container className={className} to={URL}>
      <Background className="item-bg">
        {image ? (
          <LazyLoadComponent>
            <Image {...image} />
          </LazyLoadComponent>
        ) : null}
      </Background>

      <Wrapper className="item-wrap">
        <Title className="item-title">{title}</Title>
        {pax || size ? (
          <Info row>
            {size ? (
              <Size dial={4} row>
                <Maximize />
                <Label>{size}</Label>
              </Size>
            ) : null}
            {pax ? (
              <Pax dial={4} row>
                <Users />
                <Label>{pax}</Label>
              </Pax>
            ) : null}
          </Info>
        ) : null}

        {mainService ? (
          <MainService dial={4} row>
            <Star />
            <Label>{mainService}</Label>
          </MainService>
        ) : null}

        <CTA className="item-cta" dial={4} inline row>
          {useVocabularyData('take-a-seat', languageCode)}
          <ChevronRight />
        </CTA>
      </Wrapper>
    </Container>
  )
})

const Container = styled(Link)`
  display: none;
  text-align: left;
  &.visible {
    display: block;
  }
  &:hover {
    .item-bg {
      img {
        transform: scale(1.1);
      }
    }
    .item-wrap {
      &:before {
        left: 2.875rem;
      }
    }
    .item-title {
      transform: translateX(2.75rem);
      &:before {
        transform: translateX(-2.75rem);
      }
    }
    .item-cta {
      color: ${({ theme }) => theme.colors.variants.neutralDark2};
      &:after {
        right: 0.3125rem;
      }
      svg {
        margin-left: 2.625rem;
        stroke: ${({ theme }) => theme.colors.variants.neutralDark2};
      }
    }
  }
`

const Background = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralDark2};
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  overflow: hidden;
  padding-bottom: 75%;
  position: relative;

  img {
    width: 101%;
    height: 101%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: 0.3s ease-in-out;
  }
`

const Wrapper = styled.div`
  overflow: hidden;
  padding: 2.25rem 2.625rem 2.25rem 0;
  position: relative;
  &:before {
    content: '';
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: 0;
    right: 0;
    left: 50%;
    transition: 0.3s ease-in-out;
    z-index: -1;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.875rem;
  transition: 0.3s ease-in-out;
  &:before {
    content: '';
    width: 2rem;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralDark2};
    opacity: 0.4;
    position: absolute;
    top: 0.8125rem;
    left: 0;
    transform: translateX(-2.75rem);
  }
`

const Info = styled(FlexBox)`
  margin-top: 1.5rem;

  > div {
    &:last-of-type {
      &:after {
        display: none;
      }
    }
    &:after {
      content: '';
      width: 0.125rem;
      height: 1.1875rem;
      background: ${({ theme }) => theme.colors.variants.neutralDark4};
      opacity: 0.16;
      margin: 0 0.875rem;
    }
  }

  svg {
    width: auto;
    max-width: 1.5rem;
    height: 1.125rem;
    fill: ${({ theme }) => theme.colors.variants.neutralDark4};
    margin-right: 0.625rem;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.875rem;
  opacity: 0.6;
`

const Pax = styled(FlexBox)`
  svg {
    height: 1.375rem !important;
  }
`

const Size = styled(FlexBox)``

const MainService = styled(FlexBox)`
  margin-top: 0.75rem;

  svg {
    width: auto;
    max-width: 1.5rem;
    height: 1.125rem;
    fill: ${({ theme }) => theme.colors.variants.neutralDark4};
    margin-right: 0.625rem;
  }
`

const CTA = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.secondaryLight};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.25rem;
  margin-top: 2.1875rem;
  position: relative;
  transition: 0.3s ease-in-out;
  &:after {
    content: '';
    height: 0.125rem;
    background: ${({ theme }) => theme.colors.variants.neutralDark2};
    position: absolute;
    top: 0.625rem;
    right: 1.5625rem;
    left: calc(100% - 1.5625rem);
    transition: 0.3s 0.15s ease-in-out;
  }

  svg {
    width: auto;
    height: 1rem;
    fill: none;
    stroke: ${({ theme }) => theme.colors.variants.secondaryLight};
    stroke-width: 2;
    margin-left: 2.1875rem;
    transition: 0.3s ease-in-out;
    transform: translateY(0.0625rem);
  }

  @media (max-width: 1199px) {
    font-size: 1rem;
  }
`
